/**
 * /reports/generate — formulário de geração de relatório.
 *
 * Layout 2 colunas:
 *   esquerda: form (tenant, equipments, range, tipo, seções, observações)
 *   direita:  preview compacto (contadores)
 *
 * Após gerar:
 *   - vai pra /reports/library?focus={id} pra acompanhar status
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar, FileText, Library, Sparkles } from "lucide-react";
import toast from "react-hot-toast";

import { reportsApi } from "@/api/reports";
import { apiErrorMessage } from "@/api/client";
import {
  useEquipments,
  useSites,
  useTenantCompleteness,
  useTenants,
} from "@/hooks/queries";
import { useAuth } from "@/contexts/AuthContext";
import type {
  ReportFormat,
  ReportGenerateRequest,
  ReportSection,
  ReportType,
} from "@/types";

import { PageHeader } from "@/components/PageHeader";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Field, Input, Select } from "@/components/Field";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";

const PRESETS: { label: string; days: number }[] = [
  { label: "Últimas 24h",   days: 1 },
  { label: "Últimos 7 dias", days: 7 },
  { label: "Últimos 30 dias", days: 30 },
  { label: "Últimos 90 dias", days: 90 },
];

const TYPE_OPTIONS: { value: ReportType; label: string }[] = [
  { value: "completo",     label: "Completo (todos os gráficos + estatísticas)" },
  { value: "temperatura",  label: "Apenas Temperatura" },
  { value: "eletrico",     label: "Apenas Elétrico (V/A/W)" },
  { value: "executivo",    label: "Resumo Executivo (KPIs + sumário)" },
];

const SECTIONS: { value: ReportSection; label: string }[] = [
  { value: "summary",            label: "Sumário executivo (KPIs)" },
  { value: "temperature_chart",  label: "Gráfico de temperatura" },
  { value: "electrical_chart",   label: "Gráfico elétrico (V/A/W)" },
  { value: "excursions_table",   label: "Tabela de excursões" },
  { value: "alerts_history",     label: "Histórico de ocorrências e tratativas" },
  { value: "planta",             label: "Planta da unidade com os pontos monitorados" },
  { value: "statistics",         label: "Estatísticas detalhadas" },
];

export function ReportsGeneratePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const tenants = useTenants(auth.isSuperAdmin);
  const equipments = useEquipments();
  const sites = useSites();   // pra mapear equipment → tenant

  // ---- form state ----
  const [tenantFilter, setTenantFilter] = useState("");
  const [equipmentIds, setEquipmentIds] = useState<string[]>([]);
  const [presetIdx, setPresetIdx] = useState(1);                  // 7d default
  const [periodStart, setPeriodStart] = useState<string>("");
  const [periodEnd, setPeriodEnd] = useState<string>("");
  const [reportType, setReportType] = useState<ReportType>("completo");
  const [sections, setSections] = useState<ReportSection[]>(
    SECTIONS.map((s) => s.value),
  );
  const [formats, setFormats] = useState<ReportFormat[]>(["pdf"]);
  const [notes, setNotes] = useState("");
  const [name, setName] = useState("");

  // Aplica preset selecionado
  useEffect(() => {
    if (presetIdx < 0) return;
    const days = PRESETS[presetIdx]?.days;
    if (!days) return;
    const end = new Date();
    const start = new Date(Date.now() - days * 24 * 3600 * 1000);
    setPeriodStart(toLocalInputDate(start));
    setPeriodEnd(toLocalInputDate(end));
  }, [presetIdx]);

  // Map site_id → tenant_id (Equipment só tem site_id; tenant precisa do JOIN)
  const tenantBySite = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sites.data ?? []) m.set(s.id, s.tenant_id);
    return m;
  }, [sites.data]);

  // Filtra equipments pelo tenant escolhido (super_admin)
  const filteredEquipments = useMemo(() => {
    let list = equipments.data ?? [];
    if (auth.isSuperAdmin && tenantFilter) {
      list = list.filter((e) => tenantBySite.get(e.site_id) === tenantFilter);
    }
    return list;
  }, [equipments.data, tenantFilter, tenantBySite, auth.isSuperAdmin]);

  // Quando trocar tenant, limpa equipments selecionados que não pertencem mais
  useEffect(() => {
    if (!auth.isSuperAdmin || !tenantFilter) return;
    const allowedIds = new Set(filteredEquipments.map((e) => e.id));
    setEquipmentIds((prev) => prev.filter((id) => allowedIds.has(id)));
  }, [tenantFilter, filteredEquipments, auth.isSuperAdmin]);

  // Tenant resolvido (pra completeness banner)
  const resolvedTenantId = auth.isSuperAdmin
    ? tenantFilter || null
    : auth.user?.tenant_id ?? null;
  const completeness = useTenantCompleteness(resolvedTenantId ?? undefined);

  // ---- mutation ----
  const generateMut = useMutation({
    mutationFn: (p: ReportGenerateRequest) => reportsApi.generate(p),
    onSuccess: (report) => {
      toast.success("Relatório enfileirado — acompanhe na biblioteca");
      // Invalida cache da lista pra biblioteca já abrir mostrando o novo report
      void qc.invalidateQueries({ queryKey: ["reports"] });
      navigate(`/reports/library?focus=${report.id}`);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  // ---- handlers ----
  function toggleEq(id: string) {
    setEquipmentIds((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );
  }

  function toggleSection(s: ReportSection) {
    setSections((curr) =>
      curr.includes(s) ? curr.filter((x) => x !== s) : [...curr, s],
    );
  }

  function toggleFormat(f: ReportFormat) {
    setFormats((curr) =>
      curr.includes(f) ? curr.filter((x) => x !== f) : [...curr, f],
    );
  }

  function selectAllEquipments() {
    setEquipmentIds(filteredEquipments.map((e) => e.id));
  }

  function clearEquipments() {
    setEquipmentIds([]);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (equipmentIds.length === 0) {
      toast.error("Selecione pelo menos um equipamento");
      return;
    }
    if (!periodStart || !periodEnd) {
      toast.error("Defina o intervalo de datas");
      return;
    }
    if (formats.length === 0) {
      toast.error("Selecione pelo menos um formato (PDF ou CSV)");
      return;
    }

    generateMut.mutate({
      tenant_id: resolvedTenantId,
      equipment_ids: equipmentIds,
      period_start: new Date(periodStart).toISOString(),
      period_end: new Date(periodEnd).toISOString(),
      report_type: reportType,
      sections,
      formats,
      notes: notes || null,
      name: name || null,
    });
  }

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Gerar Relatório"
        description="Configure parâmetros e gere PDF e/ou CSV pra arquivamento e auditoria."
        actions={
          <Link to="/reports/library">
            <Button variant="secondary" icon={<Library className="h-4 w-4" />}>
              Biblioteca
            </Button>
          </Link>
        }
      />

      {/* Banner cadastro incompleto */}
      {completeness.data && !completeness.data.is_regulatory_ready && (
        <Card className="mb-4 border-amber-200 dark:border-amber-900/40 !bg-amber-50 dark:!bg-amber-950/30">
          <CardBody className="py-3 text-sm text-amber-900 dark:text-amber-200">
            ⚠ Tenant com cadastro comercial incompleto. O PDF será marcado
            como <strong>"informativo"</strong>. Para fins regulatórios,
            complete em <Link className="underline" to={`/admin/tenants/${resolvedTenantId}/edit`}>Admin → Tenants</Link>.
          </CardBody>
        </Card>
      )}

      <form onSubmit={onSubmit} className="grid gap-6 lg:grid-cols-3">
        {/* ---------- Coluna esquerda — form ---------- */}
        <div className="space-y-4 lg:col-span-2">
          {/* Tenant (super_admin) */}
          {auth.isSuperAdmin && (
            <Card>
              <CardHeader title="Tenant" />
              <CardBody>
                <Field label="Tenant">
                  <Select
                    value={tenantFilter}
                    onChange={(e) => setTenantFilter(e.target.value)}
                    options={[
                      { value: "", label: "Todos (consolidado multi-tenant)" },
                      ...(tenants.data ?? []).map((t) => ({
                        value: t.id,
                        label: t.name,
                      })),
                    ]}
                  />
                </Field>
              </CardBody>
            </Card>
          )}

          {/* Equipments */}
          <Card>
            <CardHeader
              title={`Equipamentos (${equipmentIds.length} selecionado${equipmentIds.length === 1 ? "" : "s"})`}
              actions={
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={selectAllEquipments} type="button">
                    Selecionar todos
                  </Button>
                  <Button size="sm" variant="ghost" onClick={clearEquipments} type="button">
                    Limpar
                  </Button>
                </div>
              }
            />
            <CardBody>
              {filteredEquipments.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">Nenhum equipamento disponível.</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {filteredEquipments.map((e) => (
                    <label
                      key={e.id}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                        equipmentIds.includes(e.id)
                          ? "border-brand-500 bg-brand-50/40"
                          : "border-surface-200 dark:border-slate-800 hover:bg-surface-50 dark:hover:bg-slate-800/50 dark:bg-slate-800/40"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={equipmentIds.includes(e.id)}
                        onChange={() => toggleEq(e.id)}
                        className="h-4 w-4 rounded border-surface-300 dark:border-slate-700 text-brand-600"
                      />
                      <span className="font-medium text-slate-800 dark:text-slate-200">{e.name}</span>
                      <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">{e.kind ?? "—"}</span>
                    </label>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Período */}
          <Card>
            <CardHeader title="Período" subtitle="Máximo 365 dias" />
            <CardBody className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p, i) => (
                  <Button
                    key={p.label}
                    type="button"
                    size="sm"
                    variant={presetIdx === i ? "primary" : "secondary"}
                    onClick={() => setPresetIdx(i)}
                  >
                    {p.label}
                  </Button>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant={presetIdx === -1 ? "primary" : "secondary"}
                  onClick={() => setPresetIdx(-1)}
                  icon={<Calendar className="h-4 w-4" />}
                >
                  Customizado
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Início">
                  <Input
                    type="datetime-local"
                    value={periodStart}
                    onChange={(e) => { setPeriodStart(e.target.value); setPresetIdx(-1); }}
                    required
                  />
                </Field>
                <Field label="Fim">
                  <Input
                    type="datetime-local"
                    value={periodEnd}
                    onChange={(e) => { setPeriodEnd(e.target.value); setPresetIdx(-1); }}
                    required
                  />
                </Field>
              </div>
            </CardBody>
          </Card>

          {/* Tipo + Seções */}
          <Card>
            <CardHeader title="Conteúdo" />
            <CardBody className="space-y-4">
              <Field label="Tipo de relatório">
                <Select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value as ReportType)}
                  options={TYPE_OPTIONS}
                />
              </Field>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Seções</label>
                <div className="mt-2 space-y-1">
                  {SECTIONS.map((s) => (
                    <label key={s.value} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={sections.includes(s.value)}
                        onChange={() => toggleSection(s.value)}
                        className="h-4 w-4 rounded border-surface-300 dark:border-slate-700 text-brand-600"
                      />
                      {s.label}
                    </label>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Observações */}
          <Card>
            <CardHeader title="Notas (opcional)" />
            <CardBody>
              <textarea
                className="input min-h-[80px] w-full"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observações livres que vão pro rodapé do PDF..."
              />
            </CardBody>
          </Card>
        </div>

        {/* ---------- Coluna direita — preview e ação ---------- */}
        <div className="space-y-4">
          <Card>
            <CardHeader title="Resumo" />
            <CardBody className="space-y-3 text-sm">
              <Stat label="Equipamentos" value={equipmentIds.length} />
              <Stat
                label="Período"
                value={
                  periodStart && periodEnd
                    ? `${diffDays(periodStart, periodEnd)} dias`
                    : "—"
                }
              />
              <Stat label="Tipo" value={reportType} />
              <Stat label="Seções" value={sections.length} />
              <Stat
                label="Formatos"
                value={
                  <div className="flex gap-1">
                    {formats.map((f) => (
                      <Badge key={f} tone="brand">{f.toUpperCase()}</Badge>
                    ))}
                  </div>
                }
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Formatos de saída" />
            <CardBody className="space-y-2">
              {(["pdf", "csv"] as ReportFormat[]).map((f) => (
                <label key={f} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formats.includes(f)}
                    onChange={() => toggleFormat(f)}
                    className="h-4 w-4 rounded border-surface-300 dark:border-slate-700 text-brand-600"
                  />
                  <FileText className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                  {f === "pdf" ? "PDF executivo" : "CSV (raw + excursões em ZIP)"}
                </label>
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Nome do arquivo (opcional)" />
            <CardBody>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Auto-gerado se vazio"
              />
            </CardBody>
          </Card>

          <Button
            type="submit"
            className="w-full"
            loading={generateMut.isPending}
            icon={<Sparkles className="h-4 w-4" />}
          >
            Gerar relatório
          </Button>
        </div>
      </form>
    </div>
  );
}

// ---------- Helpers ----------

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="font-medium text-slate-800 dark:text-slate-200">{value}</span>
    </div>
  );
}

function toLocalInputDate(d: Date): string {
  // Formato pra <input type="datetime-local">
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function diffDays(start: string, end: string): number {
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  return Math.max(0, Math.round((e - s) / (1000 * 60 * 60 * 24)));
}
